import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { CheckIcon, LockClosedIcon, LockOpenIcon, TrashIcon } from "@heroicons/react/20/solid";
import type { MapState } from "src/types/MapState";
import { useTranslation } from "src/translations";
import { StencilBarSlider } from "src/components/StencilBarSlider";

export const StencilOverlay = observer(function StencilOverlay({
  mapState,
}: {
  mapState: MapState;
}) {
  const { stencil } = mapState;
  const { t } = useTranslation();

  const inEditMode =
    stencil.editMode && stencil.mode.kind !== "none" && stencil.mode.kind !== "wms";

  useEffect(() => {
    if (!inEditMode) return;
    mapState.enterStencilPlacement();
    return () => mapState.exitStencilPlacement();
  }, [inEditMode, mapState]);

  useEffect(() => () => stencil.setImgRef(null), [stencil]);

  if (!inEditMode) return null;

  const isFloating = stencil.mode.kind === "image-floating";
  const isLocked = stencil.mode.kind === "image-locked";

  return (
    <>
      <div className="absolute inset-x-0 top-0 h-8 bg-black/30 pointer-events-none z-[6]" />
      <div className="absolute inset-x-0 bottom-0 h-8 bg-black/30 pointer-events-none z-[6]" />

      {isFloating && stencil.mode.kind === "image-floating" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
          <img
            ref={stencil.setImgRef}
            src={stencil.mode.imageUrl}
            alt=""
            style={{
              opacity: stencil.cssOpacity,
              transform: `scale(${stencil.cssScale})`,
            }}
            className="max-w-[80vw] max-h-[80vh] object-contain select-none"
          />
        </div>
      )}

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-[7] flex items-center gap-6 bg-elevation-0 text-sm rounded border border-gray-300 shadow-md px-3 py-2 pointer-events-auto">
        <div className="flex items-center gap-4">
          <div className="w-[260px]">
            <StencilBarSlider
              label={t("stencil.opacity")}
              min={0}
              max={100}
              value={stencil.opacity.value}
              onInput={stencil.opacity.set}
              onChange={stencil.opacity.set}
            />
          </div>
          {isFloating && (
            <div className="w-[260px]">
              <StencilBarSlider
                label={t("stencil.size")}
                min={25}
                max={200}
                value={stencil.scale.value}
                onInput={stencil.scale.set}
                onChange={stencil.scale.set}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isFloating ? (
            <button
              className="flex items-center gap-1 px-3 py-1.5 rounded bg-purple-100 text-white hover:opacity-90 whitespace-nowrap"
              onClick={mapState.lockStencil}
            >
              <LockClosedIcon className="w-3 h-3" />
              {t("stencil.lock")}
            </button>
          ) : (
            <button
              className="flex items-center gap-1 px-3 py-1.5 rounded bg-purple-100 text-white hover:opacity-90 whitespace-nowrap"
              onClick={stencil.unlock}
            >
              <LockOpenIcon className="w-3 h-3" />
              {t("stencil.unlock")}
            </button>
          )}
          {isLocked && (
            <button
              className="flex items-center gap-1 px-3 py-1.5 rounded bg-purple-100 text-white hover:opacity-90 whitespace-nowrap"
              onClick={stencil.exitEditMode}
            >
              <CheckIcon className="w-3 h-3" />
              {t("stencil.done")}
            </button>
          )}
          <button
            className="flex items-center gap-1 px-3 py-1.5 rounded bg-purple-100 text-white hover:opacity-90 whitespace-nowrap"
            onClick={stencil.clear}
          >
            <TrashIcon className="w-3 h-3" />
            {t("stencil.remove")}
          </button>
        </div>
      </div>
    </>
  );
});
